import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// CategoryModal.tsx — Co Marca — Crear Categoría
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Building2 } from 'lucide-react';
import { createCategory, fetchActividadesEconomicas, fetchSucursalesList } from '../../services/categoryService';
import ImageUpload from '../Upload/ImageUpload';

interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

interface ActividadOption { id: number; codigo: string; descripcion: string; }

interface SucursalOption {
    id: number;
    nombre: string;
    activo: boolean;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#ea8406',
};

const css = `

.cm-modal * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
@keyframes cmFadeIn  { from { opacity:0; } to { opacity:1; } }
@keyframes cmSlideUp { from { opacity:0; transform:translateY(24px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
@keyframes spin { to { transform:rotate(360deg); } }
.cm-overlay  { animation: cmFadeIn  .22s ease both; }
.cm-dialog   { animation: cmSlideUp .32s cubic-bezier(.16,1,.3,1) both; }
.cm-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid var(--ad-line, #e5e7eb); background:#fafafa;
    font-size:.88rem; color:var(--ad-ink, #374151);
    outline:none; transition:all .2s;
}
.cm-input:focus { border-color:${colors.primary}; box-shadow:0 0 0 3px color-mix(in srgb, ${colors.primary} 7.06%, transparent); background:var(--ad-surface, #fff); }
.cm-textarea { resize:none; min-height:80px; }
.cm-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.cm-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.34); }
.cm-btn-primary:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; }
.cm-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-soft, #f8fafc); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.cm-btn-ghost:hover { background:var(--ad-line, #f1f5f9); }
.cm-suc-item { transition:background .15s; border-radius:9px; }
.cm-suc-item:hover { background:#f0f9ff; }
.custom-scroll::-webkit-scrollbar { width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97, 37, 123,.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97, 37, 123,.5); }
`;

const CategoryModal: React.FC<CategoryModalProps> = ({ isOpen, onClose, onCreated }) => {
    const [nombre, setNombre]       = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [imagen, setImagen]       = useState<File | null>(null);
    const [actividadEconomica, setActividadEconomica] = useState<number | ''>('');
    const [actividades, setActividades] = useState<ActividadOption[]>([]);
    const [estado, setEstado]       = useState(true);
    const [sucursalesDisponibles, setSucursalesDisponibles] = useState<SucursalOption[]>([]);
    const [selectedSucursales, setSelectedSucursales]       = useState<number[]>([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setNombre(''); setDescripcion(''); setImagen(null);
            setEstado(true); setSelectedSucursales([]); setActividadEconomica(''); setError(null);
            Promise.all([fetchSucursalesList(), fetchActividadesEconomicas()])
                .then(([sucursales, actividades]) => {
                    setSucursalesDisponibles(sucursales.filter((s: SucursalOption) => s.activo));
                    setActividades(actividades);
                })
                .catch(() => setError('No se pudieron cargar los catálogos'));
        }
    }, [isOpen]);

    const toggleSucursal = (id: number) =>
        setSelectedSucursales(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError(null);
        try {
            await createCategory({ nombre, descripcion, imagen, estado, sucursales: selectedSucursales, actividad_economica: actividadEconomica || null });
            onCreated(); onClose();
        } catch {
            setError('Error al crear la categoría');
        } finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="cm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                {/* Overlay */}
                <div className="cm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                {/* Dialog */}
                <LumaAccessiblePanel data-luma-dialog="true" className="cm-dialog custom-scroll card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'24px', width:'100%', maxWidth:'580px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid var(--ad-line, #f1f5f9)' }}>

                    {/* Barra top de color */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},${colors.accentDark},${colors.secondaryLight})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.primary},${colors.primaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px color-mix(in srgb, ${colors.primary} 18.82%, transparent)` }}>
                                    <Plus size={18} color="#fff"/>
                                </div>
                                <div>
                                    <LumaAccessibleTitle style={{ fontSize:'1.1rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Nueva Categoría</LumaAccessibleTitle>
                                    <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500' }}>Completa los datos para crear la categoría</p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px', marginTop:'4px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'; }}
                            onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'; }}>
                            <X size={15} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'18px' }}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>

                        {/* Error */}
                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`color-mix(in srgb, ${colors.primary} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:'var(--ad-link, #2854c5)', fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Nombre + Descripción */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.75rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    Nombre *
                                </label>
                                <input type="text" value={nombre} onChange={e=>setNombre(e.target.value)} required placeholder="Ej: Servicios Grupales" className="cm-input"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.75rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    Descripción
                                </label>
                                <textarea value={descripcion} onChange={e=>setDescripcion(e.target.value)} placeholder="Descripción opcional..." className="cm-input cm-textarea" style={{ height:'42px', minHeight:'42px' }}/>
                            </div>
                        </div>

                        <div>
                            <label style={{ display:'block', fontSize:'.75rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Actividad económica SIN *</label>
                            <select className="cm-input" required value={actividadEconomica} onChange={e=>setActividadEconomica(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Selecciona una actividad</option>
                                {actividades.map(a=><option key={a.id} value={a.id}>{a.codigo} - {a.descripcion}</option>)}
                            </select>
                        </div>

                        {/* Sucursales */}
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.75rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'10px' }}>
                                <Building2 size={12} color={colors.secondary}/> Sucursales activas
                            </label>
                            <div className="custom-scroll card custom-card vx-card" style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'12px', maxHeight:'140px', overflowY:'auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                                {sucursalesDisponibles.length > 0 ? sucursalesDisponibles.map(suc => {
                                    const checked = selectedSucursales.includes(suc.id);
                                    return (
                                        <label key={suc.id} className="cm-suc-item" style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', cursor:'pointer' }}>
                                            <div onClick={()=>toggleSucursal(suc.id)} style={{ width:'18px', height:'18px', borderRadius:'5px', border:`1.5px solid ${checked ? colors.secondary : '#d1d5db'}`, background:checked ? colors.secondary : '#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .15s', cursor:'pointer' }}>
                                                {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                            </div>
                                            <input type="checkbox" checked={checked} onChange={()=>toggleSucursal(suc.id)} style={{ display:'none' }}/>
                                            <span style={{ fontSize:'.82rem', color:'var(--ad-ink, #374151)', fontWeight: checked ? '700' : '500' }}>{suc.nombre}</span>
                                        </label>
                                    );
                                }) : (
                                    <p style={{ fontSize:'.78rem', color:'var(--ad-muted, #94a3b8)', gridColumn:'span 2', textAlign:'center', padding:'8px' }}>Cargando sucursales...</p>
                                )}
                            </div>
                            <p style={{ fontSize:'.7rem', color:'var(--ad-muted, #94a3b8)', marginTop:'5px' }}>Selecciona dónde estará disponible esta categoría</p>
                        </div>

                        {/* Imagen */}
                        <div>
                            <label style={{ display:'block', fontSize:'.75rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                Imagen
                            </label>
                            <ImageUpload setImagen={setImagen}/>
                        </div>

                        {/* Estado toggle */}
                        <div style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }} className="card custom-card vx-card">
                            <div>
                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'var(--ad-ink, #0f172a)' }}>Estado de la categoría</p>
                                <p style={{ fontSize:'.72rem', color: estado ? colors.secondaryLight : 'var(--ad-muted, #94a3b8)', fontWeight:'600', marginTop:'2px' }}>
                                    {estado ? '● Activa' : '○ Inactiva'}
                                </p>
                            </div>
                            <div onClick={()=>setEstado(!estado)} style={{ width:'44px', height:'24px', borderRadius:'100px', background: estado ? colors.secondary : '#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:'3px', left: estado ? '23px' : '3px', width:'18px', height:'18px', borderRadius:'50%', background:'var(--ad-surface, #fff)', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                            </div>
                        </div>

                        {/* Footer botones */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="cm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="cm-btn-primary btn btn-primary">
                                {loading
                                    ? <><span style={{ width:'13px', height:'13px', border:'2px solid rgba(255,255,255,.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }}/> Guardando...</>
                                    : <><Plus size={14}/> Crear Categoría</>
                                }
                            </button>
                        </div>
                    </form>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default CategoryModal;


